import React from 'react';
// Imports only from core @react-pdf/renderer
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

// --- Helper Functions (Copied from MUI component) ---

const formatNumber = (val) => 
    Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const isNumericHeader = (header) => 
    ['AMT', 'COMMISSION', 'SALES', 'RETURN', 'VAT', 'TOTAL', '%', 'AMOUNT', 'VALUE', 'QTY'].some(s => header.toUpperCase().includes(s));

const formatHeaderKey = (key) => key.replace(/([A-Z])/g, ' $1').trim().toUpperCase();

// --- Stylesheet for PDF Component ---

const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        padding: 20,
        fontFamily: 'Helvetica', 
        fontSize: 9, 
    },
    title: {
        fontSize: 16,
        marginBottom: 10,
        textAlign: 'center',
        fontWeight: 'bold',
    },
    table: {
        display: 'flex',
        width: 'auto',
        borderStyle: 'solid',
        borderWidth: 1,
        borderRightWidth: 0,
        borderBottomWidth: 0,
    },
    tableRow: {
        margin: 'auto',
        flexDirection: 'row',
        height: 18, 
        borderBottomColor: '#ccc',
        borderBottomWidth: 0.5,
    },
    tableHeader: {
        backgroundColor: '#e9e9e9', 
        borderBottomWidth: 1.5,
        borderBottomColor: '#000',
        fontWeight: 'bold',
        height: 25,
    },
    columnHeader: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 5,
        textAlign: 'center',
        fontWeight: 'bold',
    },
    detailCell: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 3,
        overflow: 'hidden',
    },
    numericCell: {
        textAlign: 'right',
    },
    grandTotalRow: {
        backgroundColor: '#b3b3b3',
        borderTopWidth: 2,
        borderTopColor: '#000',
        height: 25,
        fontWeight: 'bold',
        flexDirection: 'row',
    },
    grandTotalCell: {
        padding: 5,
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        fontSize: 10,
        fontWeight: 'bold',
    },
    emptyData: {
        padding: 10,
        textAlign: 'center',
        color: 'gray',
    }
});


// --- The PDF Component ---

export const SalesTableDefaultPDF = ({ rows, mode, grandColumnTotals, filters }) => {
    
    // 1. Determine Visible Columns
    const columns = rows.length > 0 ? Object.keys(rows[0]) : [];
    const visibleColumns = columns.filter(key =>
        !['isGroupHeader', 'isSubHeader', 'isSubSubHeader', 'totalAmount', 'totalAmountExclVAT', 'DEPTNO', 'TYPE', 'CASHCRED'].includes(key)
    );

    // 2. Safety Check
    if (!rows || rows.length === 0) {
        return (
            <Document title="Default Sales Report">
                <Page size="A4" style={styles.page} orientation="landscape">
                    <Text style={styles.emptyData}>No sales data found for the selected filters. Mode: {mode}</Text>
                </Page>
            </Document>
        );
    }
    
    // 3. Prepare Footer Keys
    const footerKeys = Object.keys(grandColumnTotals || {}).filter(key => visibleColumns.includes(key));
    const descriptiveSpan = visibleColumns.length - footerKeys.length;

    // 4. Dynamic Width Calculation (Simple equal division)
    const totalVisibleColumns = visibleColumns.length;
    const dynamicWidth = 100 / totalVisibleColumns;
    
    // --- Renderer Functions ---

    const renderHeader = () => (
        <View style={[styles.tableRow, styles.tableHeader]} fixed>
            {visibleColumns.map(key => (
                <Text 
                    key={key} 
                    style={[
                        styles.columnHeader, 
                        { width: `${dynamicWidth}%` },
                        isNumericHeader(key) ? styles.numericCell : {}
                    ]}
                >
                    {formatHeaderKey(key)}
                </Text>
            ))}
        </View>
    );

    const renderRow = (row, index) => (
        <View key={`default-row-${index}`} style={styles.tableRow} wrap={false}>
            {visibleColumns.map(key => {
                const cellValue = row[key];
                const isNumeric = isNumericHeader(key);

                return (
                    <Text 
                        key={key} 
                        style={[
                            styles.detailCell, 
                            { width: `${dynamicWidth}%` }, 
                            isNumeric ? styles.numericCell : {}
                        ]}
                    >
                        {isNumeric ? formatNumber(cellValue) : cellValue}
                    </Text>
                );
            })}
        </View>
    );

    const renderFooter = () => {
        if (footerKeys.length === 0) return null;

        // Calculate the combined width for the "GRAND TOTAL:" label cell
        const descriptiveSpanWidth = descriptiveSpan * dynamicWidth;

        return (
            <View style={[styles.grandTotalRow]} fixed>
                {/* Grand Total Label Cell (Spanning non-numeric columns) */}
                <Text style={[styles.grandTotalCell, { width: `${descriptiveSpanWidth}%`, textAlign: 'right' }]}>
                    GRAND TOTAL:
                </Text>

                {/* Grand Total Value Cells */}
                {footerKeys.map(key => (
                    <Text 
                        key={key} 
                        style={[
                            styles.grandTotalCell, 
                            styles.numericCell, 
                            { width: `${dynamicWidth}%` }
                        ]}
                    >
                        {formatNumber(grandColumnTotals[key])}
                    </Text>
                ))}
            </View>
        );
    }


    return (
        <Document title={`Default Sales Report - Mode: ${mode}`}>
            <Page size="A4" orientation="landscape" style={styles.page} wrap>
                <Text style={styles.title}>Sales Analysis Report{mode}</Text>
                
                {/* Optional: Include Filter Summary */}
                {filters && (
                    <View style={{ marginBottom: 10 }}>
                        <Text style={{ fontSize: 7 }}>
                            Filters: Date Range ({filters?.startDate || 'N/A'} to {filters?.endDate || 'N/A'})
                        </Text>
                    </View>
                )}

                <View style={styles.table}>
                    {renderHeader()}
                    <View>
                        {rows.map(renderRow)}
                    </View>
                </View>

                {/* Footer is rendered outside the main table View for better fixed positioning */}
                {renderFooter()}

                {/* Page Numbering */}
                <Text style={{ position: 'absolute', bottom: 10, right: 20, fontSize: 7 }} fixed>
                    Page <Text render={({ pageNumber, totalPages }) => (`${pageNumber} of ${totalPages}`)} />
                </Text>
            </Page>
        </Document>
    );
};

export default SalesTableDefaultPDF;